import { getInternalAppRoute, isNativeApp } from '../../../helpers/in-app-link.helper';

/**
 * Normalizes post/template HTML before it is bound to [innerHTML]:
 *  - Prepends `https://` to bare hostname hrefs (e.g. `www.example.com`).
 *  - Forces external `http(s):` links to open in a new tab with safe rel attrs,
 *    so a left-click in a post body doesn't evict the user from the SPA.
 *  - In the native app only, links back into School Connect are tagged with
 *    `data-inapp-route` and left without a target, so the InAppLinksDirective can
 *    route them through the Angular Router instead of the WebView opening a new
 *    window (our own links are cross-origin to the app WebView, so without this
 *    they behave like external links). On the web these keep the new-tab behavior.
 *
 * `mailto:` / `tel:` / `#anchor` / relative (`/...`) links are left alone.
 *
 * Returns the prepared HTML string. Callers are still responsible for handing
 * the result to `DomSanitizer.bypassSecurityTrustHtml` (or sanitize+bypass) as
 * they did before — this helper does not change the trust posture.
 */
export function preparePostHtml(html: string | null | undefined): string {
  if (!html) return '';

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Remove any embedded <script> tags. Designed posts render inside a sandboxed
  // iframe without `allow-scripts` (see designed-post-content), so these can never
  // execute anyway — but authors occasionally paste third-party embeds that carry a
  // <script>, and the browser logs a "Blocked script execution" error for each one.
  // Stripping them here is a visual no-op that just clears that console noise.
  tempDiv.querySelectorAll('script').forEach(script => script.remove());

  // <script> isn't the only script vector the sandbox blocks-and-logs. Inline event
  // handlers (onload/onerror/onclick/…) and `javascript:` URLs are also treated as
  // script execution, so each one produces its own "Blocked script execution" error
  // in the sandboxed iframe. Email/designer-grade HTML routinely carries these (e.g.
  // tracking pixels with onerror), so strip them here too — same visual no-op, and
  // defense-in-depth for any non-sandboxed [innerHTML] callers of this helper.
  tempDiv.querySelectorAll('*').forEach(el => {
    // Snapshot attributes first — we mutate the list while iterating.
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();
      const isEventHandler = name.startsWith('on');
      const isJsUrl =
        (name === 'href' || name === 'src' || name === 'xlink:href') &&
        /^\s*javascript:/i.test(attr.value);
      if (isEventHandler || isJsUrl) {
        el.removeAttribute(attr.name);
      }
    }
  });

  const native = isNativeApp();

  tempDiv.querySelectorAll('a[href]').forEach(link => {
    const href = link.getAttribute('href');
    if (href && !href.match(/^(https?:|mailto:|tel:|#|\/)/i)) {
      link.setAttribute('href', 'https://' + href);
    }
    const finalHref = link.getAttribute('href') || '';
    if (/^https?:/i.test(finalHref)) {
      const inAppRoute = native ? getInternalAppRoute(finalHref) : null;
      if (inAppRoute) {
        // Internal app link inside the native app: keep it in-app.
        //  - data-inapp-route: the InAppLinksDirective (and the designed-post iframe listener)
        //    read this and route via the Router.
        //  - href → same-origin relative path: the app WebView runs on a different origin than
        //    app.nhaschools.com, and iOS/WKWebView hands cross-origin anchor activations to the
        //    native layer. A same-origin relative href can't escape the app.
        //  - target="_top" (NOT removed): designed posts render in an iframe with
        //    <base target="_blank">. Removing the anchor's target lets that base turn the link
        //    into a new window, which iOS then tries to open externally and fails hard on
        //    (LSApplicationWorkspaceErrorDomain for capacitor://…). An explicit _top overrides
        //    the base so the link resolves in the top-level app context instead. In a normal
        //    (non-iframe) post _top is just the app WebView, so behavior is unchanged there.
        //    Either way the click interceptor still upgrades this to a smooth Router navigation.
        link.setAttribute('data-inapp-route', inAppRoute);
        link.setAttribute('href', inAppRoute);
        link.setAttribute('target', '_top');
      } else {
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
      }
    }
  });

  return tempDiv.innerHTML;
}
